import { TIME_SLOTS, TOTAL_MINUTES, DAY_HEIGHT } from './scheduleConstants';

export interface DayCell {
  year: number;
  month: number;
  dayOfMonth: number;
  inMonth: boolean;
}

export function getMonthMatrix(year: number, month: number): DayCell[][] {
  const firstDay = new Date(year, month, 1);
  let dow = firstDay.getDay();
  if (dow === 0) dow = 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const matrix: DayCell[][] = [];
  let currentDay = 1 - (dow - 1);

  while (true) {
    const row: DayCell[] = [];
    for (let i = 1; i <= 7; i++) {
      const dObj = new Date(year, month, currentDay);
      row.push({
        inMonth: currentDay >= 1 && currentDay <= daysInMonth,
        dayOfMonth: dObj.getDate(),
        year: dObj.getFullYear(),
        month: dObj.getMonth(),
      });
      currentDay++;
    }
    if (matrix.length === 0 || row.some((c) => c.inMonth)) {
      matrix.push(row);
    } else break;
  }
  return matrix;
}

/** Минуты с 09:00. Session.start/end — "HH:mm" или "HH:mm:ss" */
export function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const part = timeStr.slice(0, 5);
  const [hh, mm] = part.split(':').map(Number);
  if (isNaN(hh) || isNaN(mm)) return 0;
  let mins = (hh - 9) * 60 + mm;
  if (mins < 0) mins = 0;
  if (mins > TOTAL_MINUTES) mins = TOTAL_MINUTES;
  return mins;
}

/** Минуты с 09:00 → "HH:mm" */
export function buildTimeFromMinutes(mins: number): string {
  const h = 9 + Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const TIME_RANGE_MIN = 9 * 60; // 09:00 в минутах от полуночи
const TIME_RANGE_MAX = 21 * 60; // 21:00 в минутах от полуночи

/** Парсит "HH:mm" в минуты от полуночи. Возвращает null при невалидной строке. */
export function parseTimeToMinutesFromMidnight(timeStr: string): number | null {
  if (!timeStr || timeStr.length < 5) return null;
  const part = timeStr.slice(0, 5);
  const [hh, mm] = part.split(':').map(Number);
  if (isNaN(hh) || isNaN(mm) || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

/** Проверка времени сессии: 09:00–21:00 и окончание > начала. */
export function validateSessionTimes(
  startStr: string,
  endStr: string
): { ok: true } | { ok: false; error: string } {
  const start = parseTimeToMinutesFromMidnight(startStr);
  const end = parseTimeToMinutesFromMidnight(endStr);
  if (start == null || end == null) {
    return { ok: false, error: 'Укажите время начала и окончания в формате ЧЧ:ММ' };
  }
  if (start < TIME_RANGE_MIN || start > TIME_RANGE_MAX) {
    return { ok: false, error: 'Время начала должно быть в диапазоне с 09:00 до 21:00' };
  }
  if (end < TIME_RANGE_MIN || end > TIME_RANGE_MAX) {
    return { ok: false, error: 'Время окончания должно быть в диапазоне с 09:00 до 21:00' };
  }
  if (end <= start) {
    return { ok: false, error: 'Время окончания должно быть больше времени начала' };
  }
  return { ok: true };
}

/** Приводит неполный ввод к "HH:mm" (для отправки на сервер). */
export function normalizeTimeToHHmm(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 4);
  if (digits.length === 0) return '';
  if (digits.length === 1) return `${digits.padStart(2, '0')}:00`;
  if (digits.length === 2) return `${digits}:00`;
  if (digits.length === 3) return `${digits.slice(0, 2)}:${digits[2]}0`;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/** Длительность условной сессии (coef = 1): 3.5 часа. Совпадает с server SESSION_COEF_MINUTES. */
export const SESSION_COEF_MINUTES = 210;

/** Расчёт coef: duration_mins / SESSION_COEF_MINUTES (3.5ч = 1 coef), округление до 2 знаков */
export function calcCoef(startTime: string, endTime: string): number {
  const parse = (t: string) => {
    const [h, m] = (t || '00:00').slice(0, 5).split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const start = parse(startTime);
  const end = parse(endTime);
  const duration = Math.max(0, end - start);
  return Math.round((duration / SESSION_COEF_MINUTES) * 100) / 100;
}

/** Индекс студии из session.studio. "1", "2", "1 СТУДИЯ" → 0, 1, 0 */
export function getStudioIndex(studio: string): number {
  if (!studio) return 0;
  const match = studio.match(/\d+/);
  const num = match ? parseInt(match[0], 10) : 1;
  return Math.max(0, Math.min(num - 1, 5));
}

/** Диапазон дат для загрузки сессий: все видимые дни календаря (последние дни пред. месяца + первые дни след. месяца) */
export function getDateRangeForMonth(year: number, month: number): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startDate = new Date(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - 6);
  const endDate = new Date(lastDayOfMonth);
  endDate.setDate(endDate.getDate() + 6);
  return {
    from: `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`,
    to: `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`,
  };
}

export const SLOT_HEIGHT = (DAY_HEIGHT - 70) / TIME_SLOTS.length;

/** ISO дата "YYYY-MM-DD" из ячейки календаря */
export function toDayIso(cell: DayCell): string {
  return `${cell.year}-${String(cell.month + 1).padStart(2, '0')}-${String(cell.dayOfMonth).padStart(2, '0')}`;
}

/** MIME-тип для dataTransfer при D&D сессий */
export const SESSION_DRAG_TYPE = 'application/session-id';

const SLOT_MINUTES = 30;

/** Сессия-подобный объект для проверки занятости */
interface SessionLike {
  id: string;
  date: string;
  start: string;
  end: string;
  studio: string;
  /** Отменённые сессии не занимают слот для DnD и пересечений */
  is_canceled?: boolean;
}

/** Занятые слоты для дня: Set<"studioIdx-slotIdx"> */
export function getOccupiedSlots(daySessions: SessionLike[]): Set<string> {
  const occupied = new Set<string>();
  for (const s of daySessions) {
    if (s.is_canceled) continue;
    const studioIdx = getStudioIndex(s.studio);
    const startMins = parseTimeToMinutes(s.start);
    const endMins = parseTimeToMinutes(s.end);
    const startSlot = Math.floor(startMins / SLOT_MINUTES);
    const endSlot = Math.max(startSlot, Math.ceil(endMins / SLOT_MINUTES) - 1);
    for (let i = startSlot; i <= endSlot; i++) {
      occupied.add(`${studioIdx}-${i}`);
    }
  }
  return occupied;
}

/** Занят ли слот другой сессией (исключая excludeSessionId) */
export function isSlotOccupiedByOther(
  sessions: SessionLike[],
  date: string,
  studioIdx: number,
  slotIdx: number,
  excludeSessionId: string
): boolean {
  return getSessionAtSlot(sessions, date, studioIdx, slotIdx, excludeSessionId) != null;
}

/** Сессия в слоте (исключая excludeSessionId) или null */
export function getSessionAtSlot(
  sessions: SessionLike[],
  date: string,
  studioIdx: number,
  slotIdx: number,
  excludeSessionId: string
): SessionLike | null {
  const dateStr = date.slice(0, 10);
  const slotStart = slotIdx * SLOT_MINUTES;
  const slotEnd = (slotIdx + 1) * SLOT_MINUTES;
  for (const s of sessions) {
    const sDate = (s.date as string).slice(0, 10);
    if (sDate !== dateStr) continue;
    if (s.id === excludeSessionId) continue;
    if (s.is_canceled) continue;
    const sStudio = getStudioIndex(s.studio);
    if (sStudio !== studioIdx) continue;
    const sStart = parseTimeToMinutes(s.start);
    const sEnd = parseTimeToMinutes(s.end);
    if (sStart < slotEnd && sEnd > slotStart) return s;
  }
  return null;
}

/** Макс. длительность (мин) от startMins до следующей сессии или конца дня. Учитывает все пересечения. */
export function getMaxDurationAtPosition(
  sessions: SessionLike[],
  date: string,
  studioIdx: number,
  startMins: number,
  excludeSessionId: string
): number {
  const dateStr = date.slice(0, 10);
  let limit = TOTAL_MINUTES - startMins;
  for (const s of sessions) {
    const sDate = (s.date as string).slice(0, 10);
    if (sDate !== dateStr) continue;
    if (s.id === excludeSessionId) continue;
    if (s.is_canceled) continue;
    const sStudio = getStudioIndex(s.studio);
    if (sStudio !== studioIdx) continue;
    const sStart = parseTimeToMinutes(s.start);
    const sEnd = parseTimeToMinutes(s.end);
    const overlaps = sStart < startMins + limit && sEnd > startMins;
    if (!overlaps) continue;
    if (sStart <= startMins) {
      limit = 0;
      break;
    }
    limit = Math.min(limit, sStart - startMins);
  }
  return Math.max(0, limit);
}
